import { useEffect, useState, useMemo } from "react";
import { useGetEventsQuery } from "../services";
import moment from "moment";
import EventsMap from "./EventMap";
import { useWebSocket } from "../../../hooks";
import "./events.scss";
import { ListCard, MapViewFlyOut } from "../Shared";
import { displayKeyName, removeDuplicates, valueFormatter } from "../utils";
import { header } from "./tableHeader";
import { QIDragAndDropDetails } from "../../../components";
import { extractKeyValuePairs } from "../utils";
import { Dnd, MinimisedTab, useDndContainer, WindowTab } from "../Dnd";
import { FMDPStorage } from "../../../shared/helper";
import { useVariableDataQuery } from "../../../hooks/QIVariableDataQuery";
import { getLocalizedString } from "../../../shared/translation";
import { useGetPluginsQuery } from "../../ManagementPortal/services";
import { PluginContainer } from "../../Plugins";
import Scrollbars from "react-custom-scrollbars-2";
import { useSelector, useDispatch } from "react-redux";
import { setGlobalResetCount, setCurrentTabGlobal } from "../../../reduxStore/features";
import { EventsAdvanceSearch } from "./AdvanceSearch";
import { hasAdminRole } from "../utils";

export const EventsAlternateContainer = () => {
  const selectedPlateNumbers = FMDPStorage.get("selected-plate-numbers")?.length
    ? FMDPStorage.get("selected-plate-numbers")
    : [""];
  const { variableData } = useVariableDataQuery();

  const [deviceId, setDeviceId] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const [mapPoint, setMapPoint] = useState(null);
  const [eventSearch, setEventSearch] = useState("");
  const [advanceSearch, setAdvanceSearch] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const [selectedRangeLocal, setSelectedRangeLocal] = useState({
    startDate: moment().subtract(24, "hours"),
    endDate: moment(),
  });
  const [resetCount, setResetCount] = useState(0);
  const [clientId, setClientId] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [allEventsData, setAllEventsData] = useState({});
  const [mapExpand, setMapExpand] = useState(false);
  const [showEventsDetails, setShowEventsDetails] = useState(false);
  const [dockAlign, setDockAlign] = useState("cross");
  const [showTrack, setShowTrack] = useState(false);
  const [maximizeMinimize, setmaximizeMinimize] = useState(false);
  const [currentTab, setCurrentTab] = useState("Map");
  const [isLiveFeedCount, setIsLiveFeedCount] = useState(0);
  const [isLiveFeed, setIsLiveFeed] = useState(false);
  const dispatch = useDispatch();
  let globalDockAlign = useSelector((state) => state.common.globalDockAlign);

  const dateRangeOptions = useMemo(() => {
    return [1, 2, 6, 12, 24];
  }, []);

  let selectedRange = useSelector((state) => state.common.selectedRange);
  let globalResetCount = useSelector((state) => state.common.globalResetCount);
  let configJson = useSelector((state) => state.common.globalCustomJson);

  const { activeBoxes, addNewBox, removeBox, revertBox, isItemsDragging, handleDragChange } =
    useDndContainer();

  const pagination = { page, perPage, onPageChange: setPage, onPerPageChange: setPerPage };

  const { realTimedata } = useWebSocket(
    String(clientId),
    "event",
    "sub_event",
    null,
    "source_id",
    null,
    null,
    true
  );

  const currentUser = FMDPStorage.get("current-user");

  const selectedAppId = FMDPStorage.get("selected-app-id");
  const [mapProvider, setMapProvider] = useState();
  const [mapboxAccessToken, setMapBoxAccessToken] = useState();
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState();

  const getFilterList = (type) => {
    const isAdmin = hasAdminRole(currentUser, selectedAppId);

    if (type === "checkAdmin") {
      return isAdmin;
    } else {
      if (!isAdmin && type === "device") {
        return "";
      } else if (!isAdmin && type === "plateNo") {
        return selectedPlateNumbers?.length > 0 ? selectedPlateNumbers : "";
      } else {
        return "";
      }
    }
  };

  // Filter clearing functions
  const handleClearFilter = (filter) => {
    console.log(filter);
    if (filter.type === "advance") {
      // Clear specific advance search field
      const updatedAdvanceSearch = { ...advanceSearch };
      delete updatedAdvanceSearch[filter.key];

      // If no fields remain, clear the entire advance search
      if (Object.keys(updatedAdvanceSearch).length === 0) {
        setAdvanceSearch(null);
      } else {
        setAdvanceSearch(updatedAdvanceSearch);
      }
    } else if (filter.type === "simple") {
      setEventSearch("");
    } else if (filter.type === "plates") {
      FMDPStorage.set("selected-plate-numbers", []);
      // Force component re-render by updating a state
      setPage(1);
    }
  };

  const handleClearAllFilters = () => {
    setAdvanceSearch(null);
    setEventSearch("");
    FMDPStorage.set("selected-plate-numbers", []);
    setPage(1);
  };

  //If search changes, reset page to 1
  useEffect(() => {
    if (eventSearch || advanceSearch) {
      setPage(1);
    }
  }, [eventSearch, advanceSearch]);

  //If selectedRange changes, reset page to 1
  useEffect(() => {
    setPage(1);
  }, [selectedRange]);

  //Get Events data
  const {
    data: eventsData,
    error,
    isLoading,
    refetch,
  } = useGetEventsQuery(
    clientId
      ? {
          isAdmin: getFilterList("checkAdmin"),
          startTime: moment(selectedRange.startDate).valueOf(),
          endTime: moment(selectedRange.endDate).valueOf(),
          // deviceIds: getFilterList("device"),
          // Only send plate_number when advanced search is not active and we have valid plate numbers
          ...(selectedPlateNumbers.length > 0 &&
            selectedPlateNumbers.some((p) => p && p.trim() !== "") &&
            !advanceSearch && {
              plate_number: selectedPlateNumbers.filter((p) => p && p.trim() !== ""),
            }),
          search: eventSearch && eventSearch.trim() !== "" && !advanceSearch,
          clientId: clientId,
          //Page count starts from zero.
          page: page - 1,
          perPage: perPage,
          // Only send q parameter when advanced search is not active
          ...(eventSearch && !advanceSearch && { q: eventSearch.toUpperCase() }),
          advanceSearch: advanceSearch,
        }
      : null
  );

  //Modify Data with Api values
  useEffect(() => {
    if (eventsData) {
      setAllEventsData((prev) => {
        return { ...prev, ...eventsData };
      });
    }
  }, [eventsData]);

  //Modify Data RealTime values
  useEffect(() => {
    if (realTimedata && selectedRowId === null && !eventSearch && !advanceSearch) {
      // Stop real-time updates when a row is selected
      const newEventsData = Object?.values(realTimedata)?.filter(
        (item) => item?.packet_type === "event"
      );

      // Check if the selected range ends near current time and duration is in dateRangeOptions
      const now = moment();
      const timeDiff = moment(selectedRange.endDate).diff(selectedRange.startDate, "hours");
      const isEndTimeNearNow = Math.abs(moment(selectedRange.endDate).diff(now, "minutes")) <= 60;
      const isDurationValid = dateRangeOptions.includes(timeDiff);

      if (page === 1 && isDurationValid && isEndTimeNearNow) {
        setIsLiveFeed(true);
        if (newEventsData.length > 0) {
          setAllEventsData((prev) => ({
            ...prev,
            events: newEventsData.concat(prev.events),
          }));
          setIsLiveFeedCount((prev) => prev + 1);
        }
      } else {
        setIsLiveFeed(false);
      }
    }
  }, [realTimedata, page, selectedRange, selectedRowId, eventSearch, advanceSearch]);

  //Get Client ID
  useEffect(() => {
    const selectedClientId = JSON.parse(localStorage.getItem("selected-app-id"));
    const currentUser = JSON.parse(localStorage.getItem("current-user"))?.client_apps?.find(
      (item) => {
        return item.id === selectedClientId;
      }
    );

    setClientId(currentUser?.client?.id);
    //cleanup
    return () => setClientId(null);
  }, []);

  //clean up calendar value on compoonent unmount
  useEffect(() => {
    return setResetCount((prev) => prev + 1);
  }, []);

  useEffect(() => {
    setHighlight(null);
    removeRowSelection();
  }, [selectedRange]);

  // Add this effect to get map configuration
  useEffect(() => {
    const selectedClientId = JSON.parse(localStorage.getItem("selected-app-id"));
    const currentUser = JSON.parse(localStorage.getItem("current-user"))?.client_apps?.find(
      (item) => {
        return item.id === selectedClientId;
      }
    );

    try {
      if (currentUser?.client?.config_json) {
        const mapProvider = configJson?.tracking?.web?.map;

        if (mapProvider?.provider === "mapbox" && mapProvider?.mapboxAccessToken) {
          setMapProvider("mapbox");
          setMapBoxAccessToken(mapProvider.mapboxAccessToken);
          setGoogleMapsApiKey(null);
        } else if (mapProvider?.provider === "google" && mapProvider?.googleMapsApiKey) {
          setMapProvider("google");
          setMapBoxAccessToken(null);
          setGoogleMapsApiKey(mapProvider.googleMapsApiKey);
        } else {
          setMapProvider("qiMap");
          setMapBoxAccessToken(null);
          setGoogleMapsApiKey(null);
        }
      }
    } catch (e) {
      console.log(e);
    }
  }, []);

  const removeRowSelection = () => {
    setSelectedRowId(null);
    setDeviceId(null);
    setMapPoint(null);
    setShowEventsDetails(false);
    setDockAlign("cross");
  };

  const onRowClick = (data, id) => {
    if (selectedRowId === id) {
      // deselect if same row clicked
      removeRowSelection();
    } else {
      // select row
      setSelectedRowId(id);
      setDeviceId(data.source_id);
      if (data?.events?.type === "geofence" && ["exit", "entry"].includes(data?.events?.status)) {
        setMapPoint(
          data?.events[data?.events?.status === "entry" ? "entry_position" : "exit_position"]
        );
      } else {
        setMapPoint(data?.gps?.position);
      }

      if (data?.events?.type === "geofence" && data?.events?.sub_type === "time limit exceeded") {
        setMapPoint(data?.events?.start_position);
      }

      setShowEventsDetails(data);
      dockAlign === "cross" && setDockAlign(globalDockAlign);

      globalDockAlign === "pop-out" && addNewBox(100);
    }
  };

  const keyValues = extractKeyValuePairs(showEventsDetails || []);

  const renderDetails = keyValues.map((pair) => {
    const key = Object.keys(pair)[0];
    let value = pair[key];

    return (
      <>
        {value !== null && !key.startsWith("_") && (
          <li className="details-block_other-data_item">
            <strong title={key} className="header truncate">
              {getLocalizedString(key, displayKeyName(variableData, key))}
            </strong>
            <span title={valueFormatter(key, value, variableData)} className="body truncate">
              {valueFormatter(key, value, variableData)}
            </span>
          </li>
        )}
      </>
    );
  });

  const getListDetails = () => {
    return (
      <section className="details-block_data-container events-details-list">
        <Scrollbars>
          <ul className="qi-list-view details-block_data details-block_other-data">
            {renderDetails}
          </ul>
        </Scrollbars>
      </section>
    );
  };

  const openDeviceModal = () => {
    setShowDetails(false);
    addNewBox(100);
  };

  const funSetActive = () => {
    setCurrentTab("Map");
  };

  const showdetailsTabs = (tab) => {
    setCurrentTab(tab);
  };

  const { data: dataPlugins = { data: [], total_count: 0 } } = useGetPluginsQuery({
    page,
    per_page: perPage,
    order_by: "plugins.created_at",
    order_dir: "desc",
  });

  const addPlugins = dataPlugins.data.filter(
    (plugin) =>
      plugin.tracker_containers.map((s) => parseInt(s, 10)).includes(5) && plugin.is_enabled
  );
  const isPluginTab = addPlugins.some((plugin) => plugin.name === currentTab);
  const selectedPlugin = addPlugins.find((plugin) => plugin.name == currentTab);

  useEffect(() => {
    dispatch(setCurrentTabGlobal(currentTab));
  }, [currentTab]);

  useEffect(() => {
    if (page) {
      setSelectedRowId(null);
      setMapPoint(null);
      setShowEventsDetails(false);
      setDockAlign("cross");
    }
  }, [page]);

  // When used advance search, clear simple search
  useEffect(() => {
    if (advanceSearch !== null) {
      setEventSearch("");
    }
  }, [advanceSearch]);

  return (
    <>
      <div className="tracking-events">
        <div className="tracking-app-main-container">
          <MapViewFlyOut
            data={removeDuplicates(allEventsData?.events || [])}
            isLiveFeedCount={isLiveFeedCount}
            isLiveFeed={isLiveFeed}
            pagination={{ ...pagination, count: allEventsData?.total_count }}
            deviceId={selectedRowId}
            resetCount={resetCount}
            setResetCount={setResetCount}
            selectedRange={selectedRange}
            setSelectedRange={setSelectedRangeLocal}
            error={error}
            setDeviceId={setDeviceId}
            highlight={highlight}
            setHighlight={setHighlight}
            setMapPoint={setMapPoint}
            setMapExpand={setMapExpand}
            listType={"Events"}
            page={page}
            perPage={perPage}
            setPage={setPage}
            setPerPage={setPerPage}
            setShowDetails={setShowEventsDetails}
            showDetails={showEventsDetails}
            setDockAlign={setDockAlign}
            simpleSearch={eventSearch}
            disableSimpleSearch={advanceSearch === null ? false : true}
            setSimpleSearch={setEventSearch}
            advanceSearch={advanceSearch}
            setAdvanceSearch={setAdvanceSearch}
            AdvanceSearchComponent={EventsAdvanceSearch}
            advanceSearchProps={{ events: allEventsData?.events || [] }}
            selectedPage={"Events"}
            title={getLocalizedString("events", "Events")}
            header={header}
            isLoading={isLoading}
            search_by_message={getLocalizedString(
              "search_by_plate_no_event_name_type_subtype_status_device_name_driver_name_device_imei",
              "Plate No, Event Name, Type, Sub Type, Status, Device Name, Driver Name, Device IMEI"
            )}
            setDetails={setShowEventsDetails}
            setSelectedRowId={setSelectedRowId}
            selectedPlateNumbers={selectedPlateNumbers}
            onClearFilter={handleClearFilter}
            onClearAllFilters={handleClearAllFilters}
          >
            <Scrollbars>
              <ListCard
                data={allEventsData?.events || []}
                cardType="Events"
                onRowClick={onRowClick}
                highlight={highlight}
                setHighlight={setHighlight}
                selectedRowId={selectedRowId}
              />
            </Scrollbars>
          </MapViewFlyOut>

          <article className="tracking-app-main-container_block ${currentTab}">
            {addPlugins?.length > 0 && (
              <nav className="map-view-tabs">
                <ul className="map-and-details_tabs">
                  {addPlugins.map((plugin, index) => (
                    <li key={index}>
                      <span
                        className={`map-and-details_tabs_item ${
                          currentTab === plugin.name && "active"
                        }`}
                        onClick={() => showdetailsTabs(plugin.name)}
                      >
                        {getLocalizedString("plugins", plugin.name)}
                      </span>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
            <article
              className={`map-and-details events-map ${dockAlign} ${
                maximizeMinimize ? "minimized-details" : ""
              }`}
            >
              {currentTab === "Map" ? (
                <EventsMap
                  dockAlign={dockAlign}
                  mapExpand={mapExpand}
                  deviceId={deviceId}
                  mapPoint={mapPoint}
                  selectedRowId={selectedRowId}
                  showEventsDetails={showEventsDetails}
                  setDockAlign={setDockAlign}
                  maximizeMinimize={maximizeMinimize}
                  mapProvider={mapProvider}
                  mapboxAccessToken={mapboxAccessToken}
                  googleMapsApiKey={googleMapsApiKey}
                  eventsData={allEventsData?.events || []}
                  onEventMarkerClick={(eventData, eventIndex) => {
                    // Handle event marker click - same logic as row click
                    onRowClick(eventData, eventIndex);
                  }}
                />
              ) : isPluginTab ? (
                <PluginContainer selectedPlugin={selectedPlugin} />
              ) : null}
              {showEventsDetails && (
                <article className={`details-block`}>
                  <QIDragAndDropDetails
                    setDetails={setShowEventsDetails}
                    deviceData={showEventsDetails || {}}
                    setPopup={openDeviceModal}
                    removeRowSelection={removeRowSelection}
                    setDockAlign={setDockAlign}
                    dockAlign={dockAlign}
                    deviceId={deviceId}
                    realTimedata={realTimedata}
                    setDeviceId={setDeviceId}
                    showTrack={showTrack}
                    setShowTrack={setShowTrack}
                    headerName={getLocalizedString("plate_number", "Plate Number")}
                    listDetails={getListDetails()}
                    selectedVehiclePlate={showEventsDetails?.plate_number}
                    maximizeMinimize={maximizeMinimize}
                    setmaximizeMinimize={setmaximizeMinimize}
                  />
                </article>
              )}
            </article>
          </article>
          <div>
            <MinimisedTab layoutName="trackingLayout" />
          </div>
        </div>
        {dockAlign === "pop-out" && (
          <Dnd
            layoutName="trackingLayout"
            isItemsDragging={isItemsDragging}
            handleDragChange={handleDragChange}
            setDetails={setShowDetails}
          >
            {Object.keys(activeBoxes).map((key) => (
              <WindowTab
                key={key}
                id={key}
                label={key}
                {...activeBoxes[key].props}
                onClose={removeBox}
                onMinimise={revertBox}
                setShowDetails={setShowDetails}
                setDockAlign={setDockAlign}
                setDeviceId={setDeviceId}
                setShowTrack={setShowTrack}
                selectedVehiclePlate={showEventsDetails?.plate_number}
                setHighlight={setHighlight}
                removeLayerOnClose={false}
                removeRowSelection={removeRowSelection}
              >
                {getListDetails()}
              </WindowTab>
            ))}
          </Dnd>
        )}
      </div>
    </>
  );
};
